//myutms/packages/plan-catalog/pricingPresentationContract.js
/* packages/plan-catalog/pricingPresentationContract.js */
/* eslint max-len: ["error", { "code": 80 }] */

/**
 * PRICING PRESENTATION / CONTENT CONTRACT
 *
 * PURPOSE
 * -------
 * This module defines the NON-BILLING presentation/content layer for pricing.
 *
 * It exists because we need a scalable way to manage:
 *   • plan marketing subtitles
 *   • "best for" copy
 *   • feature intro copy
 *   • feature labels
 *   • tooltips
 *   • compare categories
 *   • compare ordering
 *   • card feature ordering
 *   • which plans include which presentation-level features
 *
 * IMPORTANT SEPARATION OF RESPONSIBILITIES
 * ---------------------------------------
 * This file is intentionally separate from packages/plan-catalog/index.js.
 *
 * Why:
 * - plan-catalog/index.js should remain the billing / entitlement contract:
 *     • Stripe price IDs
 *     • environment-aware billing config
 *     • self-serve vs contact-sales behavior
 *     • seat limits
 *     • entitlements
 *
 * - THIS file is the presentation/content contract:
 *     • what pricing pages display
 *     • how compare tables are grouped
 *     • which features are highlighted in cards
 *
 * This separation keeps billing-critical logic stable while allowing the
 * presentation layer to evolve independently.
 *
 * PHASE 1 GOAL
 * ------------
 * Phase 1 does NOT yet replace your current runtime behavior.
 * Instead, this file establishes the contract that future admin UI,
 * Firestore persistence, and merge/generation scripts will follow.
 *
 * In other words:
 * - This is the schema + defaults + guardrails.
 * - Later, Firestore documents should conform to this contract.
 * - Later, merge scripts will use this contract to generate public JSON.
 *
 * ENTERPRISE-GRADE ACCESS MODEL
 * -----------------------------
 * For now, only superAppadmin may modify or publish pricing presentation
 * content. This is intentionally conservative because pricing presentation
 * still affects:
 *   • public pricing claims
 *   • plan positioning
 *   • compare matrix semantics
 *   • conversion experience
 *
 * We expose helper functions so the app UI and future publish flow can
 * consistently gate access.
 *
 * DESIGN PRINCIPLES
 * -----------------
 * 1. Do NOT duplicate text definitions across cards + compare if avoidable.
 * 2. Define reusable feature records once by stable feature ID.
 * 3. Let plans reference those feature IDs for card display.
 * 4. Let compare categories reference/order those same feature IDs.
 * 5. Be tolerant of partial/missing data during rollout.
 * 6. Preserve backwards compatibility while migrating from hard-coded WP data.
 *
 * WHAT THIS FILE EXPORTS
 * ----------------------
 * - ALLOWED_PRICING_PRESENTATION_EDITOR_ROLES
 * - PRICING_PRESENTATION_DOC_PATH
 * - PRICING_PRESENTATION_VERSION
 * - PRICING_PRESENTATION_DEFAULTS
 * - helper functions for:
 *     • permission checks
 *     • default object creation
 *     • validation
 *     • normalization
 *
 * WHY THERE ARE DEFAULTS / "HARDCODED" VALUES HERE
 * ------------------------------------------------
 * These values are NOT intended to be the final day-to-day editing workflow.
 *
 * They exist so we have:
 * - a bootstrap contract before the admin UI exists
 * - fallback data if Firestore is empty
 * - safe defaults for validation + publish flows
 *
 * Once the pricing-content admin UI exists, Firestore-managed content should
 * override these defaults. These remain the seed/fallback/reference layer.
 *
 * FIRESTORE SHAPE (TARGET)
 * ------------------------
 * Recommended document path:
 *   systemConfig/pricingPresentation
 *
 * Recommended shape:
 * {
 *   version,
 *   updatedAt,
 *   updatedBy,
 *   planContent,
 *   features,
 *   compareCategories
 * }
 *
 * NOTE
 * ----
 * This module uses CommonJS exports so it can be consumed by:
 *   • Node scripts
 *   • Cloud/merge utilities
 *   • the app (via existing tooling that already imports plan-catalog)
 */

/* ──────────────────────────────────────────────────────────────
 * Constants
 * ─────────────────────────────────────────────────────────── */

const PRICING_PRESENTATION_VERSION = 1;

/**
 * Firestore document path recommendation for the pricing presentation draft.
 *
 * Keeping this as a single doc for phase 1 simplifies:
 * - loading
 * - validation
 * - publishing
 * - change auditing
 *
 * Later, if the model grows, you can split into multiple docs/collections.
 */
const PRICING_PRESENTATION_DOC_PATH = "systemConfig/pricingPresentation";

/**
 * For now, only superAppadmin may edit/publish.
 *
 * This should stay narrow until:
 * - the UI is stable
 * - publish flow is audited
 * - rollback / preview workflows are proven
 */
const ALLOWED_PRICING_PRESENTATION_EDITOR_ROLES = [
  "superAppadmin",
];

/**
 * Canonical plan keys expected across the pricing system.
 * Keep aligned with packages/plan-catalog/index.js base plans.
 */
const PRICING_PRESENTATION_PLAN_KEYS = [
  "standard",
  "pro",
  "enterprise",
];

/**
 * Default compare category ordering.
 *
 * These are presentation/publishing categories, not billing categories.
 * Labels can later be changed in Firestore while keys remain stable.
 *
 * Per your latest request, we start with:
 * - Core Marketing Form Features
 * - Asset Management Features
 * - Data Management
 * - Integrations
 * - Admin Features
 * - Share
 * - Account Management
 * - Support
 */
const DEFAULT_COMPARE_CATEGORIES = [
  {
    key: "core_marketing_form_features",
    label: "Core Marketing Form Features",
    order: 10,
  },
  {
    key: "asset_management_features",
    label: "Asset Management Features",
    order: 20,
  },
  {
    key: "data_management",
    label: "Data Management",
    order: 30,
  },
  {
    key: "integrations",
    label: "Integrations",
    order: 40,
  },
  {
    key: "admin_features",
    label: "Admin Features",
    order: 50,
  },
  {
    key: "share",
    label: "Share",
    order: 60,
  },
  {
    key: "account_management",
    label: "Account Management",
    order: 70,
  },
  {
    key: "support",
    label: "Support",
    order: 80,
  },
];

/* ──────────────────────────────────────────────────────────────
 * Default presentation content
 * ─────────────────────────────────────────────────────────── */

/**
 * Plan-level presentation defaults.
 *
 * These fields are presentation-only.
 * They should NOT control billing or entitlements.
 *
 * NOTE:
 * - You said you are removing the user-limit messaging from the pricing page.
 * - Therefore we intentionally DO NOT include "starts at 10 users" style
 *   presentation features in these defaults.
 * - Billing constraints in plan-catalog/index.js remain unchanged for now.
 */
const DEFAULT_PLAN_CONTENT = {
  standard: {
    subtitle: "Great for small-medium teams:",
    bestFor: "Best for small teams",
    featureIntro: "All features for URL management:",
    cardFeatureIds: [
      "unlimited_utm_form_types",
      "custom_parameter_rules",
      "custom_utm_groupings",
      "form_templates",
      "submission_analytics",
      "public_private_sharing",
      "email_support",
    ],
  },

  pro: {
    subtitle: "Everything in Standard, plus:",
    bestFor: "Best for growing businesses",
    featureIntro: "Everything in Standard, plus:",
    cardFeatureIds: [
      "asset_level_utm_tracking",
      "digital_asset_management_lite",
      "digital_asset_tagging",
      "submission_analytics",
      "priority_support",
      "bitly_integration_click_tracking",
      "google_looker_addon_available",
    ],
  },

  enterprise: {
    subtitle: "Includes Pro, plus:",
    bestFor: "Best for enterprise organizations",
    featureIntro: "Everything in Pro, plus:",
    cardFeatureIds: [
      "org_wide_field_management",
      "invoice_billing",
      "priority_zoom_support",
      "sso_addon_available",
      "custom_feature_dev_available",
    ],
  },
};

/**
 * Feature registry defaults.
 *
 * ENTERPRISE NOTE:
 * These are presentation features, not entitlement flags.
 * A feature can appear in cards/compare because it is part of your
 * pricing-positioning language even if the deeper entitlement model
 * is more nuanced elsewhere.
 *
 * `includedIn` intentionally mirrors plan membership for compare display.
 * That lets cards and compare table derive from one reusable feature record.
 */
const DEFAULT_FEATURES = {
  unlimited_utm_form_types: {
    label: "Unlimited UTM form types",
    tooltip:
      "Create any number of custom UTM-specific forms for your projects.",
    categoryKey: "core_marketing_form_features",
    compareOrder: 10,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  custom_parameter_rules: {
    label: "Custom parameter rules",
    tooltip: "Limit capitalization, spacing, and more.",
    categoryKey: "core_marketing_form_features",
    compareOrder: 20,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  custom_utm_groupings: {
    label: "Custom UTM groupings",
    tooltip: "Customize how UTMs are concatenated.",
    categoryKey: "core_marketing_form_features",
    compareOrder: 30,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  form_templates: {
    label: "Templates",
    tooltip:
      "App-wide or publish your own organization-wide form templates.",
    categoryKey: "core_marketing_form_features",
    compareOrder: 40,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  submission_analytics: {
    label: "Submission analytics",
    tooltip: "View form submissions from one place.",
    categoryKey: "data_management",
    compareOrder: 50,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  asset_level_utm_tracking: {
    label: "Asset-level UTM tracking",
    tooltip:
      "Track tagged assets alongside campaign link governance.",
    categoryKey: "asset_management_features",
    compareOrder: 60,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: true,
    },
  },

  digital_asset_management_lite: {
    label: "Digital Asset Management-lite",
    tooltip: "Manage digital assets from one central place.",
    categoryKey: "asset_management_features",
    compareOrder: 70,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: true,
    },
  },

  digital_asset_tagging: {
    label: "Digital asset tagging",
    tooltip:
      "Tag digital assets with UTM parameters for return-on-asset workflows.",
    categoryKey: "asset_management_features",
    compareOrder: 80,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: true,
    },
  },

  google_looker_addon_available: {
    label: "Google Looker integration add-on available",
    tooltip: "Contact us for more details.",
    categoryKey: "integrations",
    compareOrder: 90,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: false,
    },
  },

  bitly_integration_click_tracking: {
    label: "Bitly integration with click tracking",
    tooltip: "Bitly integration with click tracking.",
    categoryKey: "integrations",
    compareOrder: 100,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: false,
    },
  },

  user_roles: {
    label: "User roles",
    tooltip: "Define view-only or editor permissions.",
    categoryKey: "admin_features",
    compareOrder: 110,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  org_wide_field_management: {
    label: "Org-wide field management",
    tooltip: "Manage fields for the entire organization.",
    categoryKey: "admin_features",
    compareOrder: 120,
    includedIn: {
      standard: false,
      pro: false,
      enterprise: true,
    },
  },

  public_private_sharing: {
    label: "Public and private sharing",
    tooltip:
      "Share forms publicly, organization-wide, or with specific users.",
    categoryKey: "share",
    compareOrder: 130,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  invoice_billing: {
    label: "Invoice billing",
    tooltip: "",
    categoryKey: "account_management",
    compareOrder: 140,
    includedIn: {
      standard: false,
      pro: false,
      enterprise: true,
    },
  },

  email_support: {
    label: "Email support",
    tooltip: "",
    categoryKey: "support",
    compareOrder: 150,
    includedIn: {
      standard: true,
      pro: true,
      enterprise: true,
    },
  },

  priority_support: {
    label: "Priority support",
    tooltip: "Priority responses in less than 48 hours.",
    categoryKey: "support",
    compareOrder: 160,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: true,
    },
  },

  priority_zoom_support: {
    label: "Priority support with live Zoom support",
    tooltip: "",
    categoryKey: "support",
    compareOrder: 170,
    includedIn: {
      standard: false,
      pro: false,
      enterprise: true,
    },
  },

  sso_addon_available: {
    label: "SSO add-on available",
    tooltip: "Contact us about adding SSO.",
    categoryKey: "support",
    compareOrder: 180,
    includedIn: {
      standard: false,
      pro: false,
      enterprise: true,
    },
  },

  custom_feature_dev_available: {
    label: "Custom feature development available",
    tooltip: "",
    categoryKey: "support",
    compareOrder: 190,
    includedIn: {
      standard: false,
      pro: true,
      enterprise: true,
    },
  },
};

/**
 * Full default document.
 *
 * This is the baseline contract the admin UI, Firestore helpers, and future
 * merge scripts should expect.
 */
const PRICING_PRESENTATION_DEFAULTS = {
  version: PRICING_PRESENTATION_VERSION,
  updatedAt: null,
  updatedBy: {
    uid: null,
    email: null,
    role: null,
  },
  planContent: DEFAULT_PLAN_CONTENT,
  features: DEFAULT_FEATURES,
  compareCategories: DEFAULT_COMPARE_CATEGORIES,
};

/* ──────────────────────────────────────────────────────────────
 * Utility helpers
 * ─────────────────────────────────────────────────────────── */

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeString(value, fallback = "") {
  if (typeof value !== "string") return fallback;
  return value.trim();
}

function normalizeNumber(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function sortByOrder(a, b) {
  return normalizeNumber(a?.order, 0) - normalizeNumber(b?.order, 0);
}

/* ──────────────────────────────────────────────────────────────
 * Permission helpers
 * ─────────────────────────────────────────────────────────── */

/**
 * Extract a role string from a user/profile-like object.
 *
 * Supports a few likely shapes:
 * - user.role
 * - user.userRole
 * - user.profile.role
 * - user.claims.role
 */
function getPricingPresentationRole(actor) {
  if (!actor || typeof actor !== "object") return null;

  return (
    actor.role ||
    actor.userRole ||
    (actor.profile && actor.profile.role) ||
    (actor.claims && actor.claims.role) ||
    null
  );
}

/**
 * True only for approved pricing-presentation editor roles.
 */
function canEditPricingPresentation(actor) {
  const role = getPricingPresentationRole(actor);
  return ALLOWED_PRICING_PRESENTATION_EDITOR_ROLES.includes(role);
}

/**
 * For phase 1, read follows the same conservative role gate as write.
 * You can relax this later for preview/read-only roles.
 */
function canReadPricingPresentation(actor) {
  return canEditPricingPresentation(actor);
}

/* ──────────────────────────────────────────────────────────────
 * Default builders
 * ─────────────────────────────────────────────────────────── */

function createDefaultPricingPresentationDoc() {
  return deepClone(PRICING_PRESENTATION_DEFAULTS);
}

function createDefaultPlanContent(planKey) {
  const defaults =
    PRICING_PRESENTATION_DEFAULTS.planContent[planKey] || {
      subtitle: "",
      bestFor: "",
      featureIntro: "Included features:",
      cardFeatureIds: [],
    };

  return deepClone(defaults);
}

function createDefaultFeatureRecord(featureId = "") {
  return {
    label: normalizeString(featureId),
    tooltip: "",
    categoryKey: "",
    compareOrder: 0,
    includedIn: {
      standard: false,
      pro: false,
      enterprise: false,
    },
  };
}

/* ──────────────────────────────────────────────────────────────
 * Validation
 * ─────────────────────────────────────────────────────────── */

/**
 * Validate one feature record.
 *
 * Returns:
 * {
 *   ok: boolean,
 *   errors: string[]
 * }
 */
function validateFeatureRecord(featureId, feature) {
  const errors = [];

  if (!normalizeString(featureId)) {
    errors.push("Feature ID is required.");
  }

  if (!isPlainObject(feature)) {
    errors.push(`Feature "${featureId}" must be an object.`);
    return {ok: false, errors};
  }

  if (!normalizeString(feature.label)) {
    errors.push(`Feature "${featureId}" must include a label.`);
  }

  if (!normalizeString(feature.categoryKey)) {
    errors.push(`Feature "${featureId}" must include a categoryKey.`);
  }

  if (!isPlainObject(feature.includedIn)) {
    errors.push(`Feature "${featureId}" must include includedIn object.`);
  } else {
    PRICING_PRESENTATION_PLAN_KEYS.forEach((planKey) => {
      if (typeof feature.includedIn[planKey] !== "boolean") {
        errors.push(
            `Feature "${featureId}" includedIn.${planKey} must be boolean.`,
        );
      }
    });
  }

  return {ok: errors.length === 0, errors};
}

/**
 * Validate the full pricing presentation doc.
 *
 * This is intentionally pragmatic, not over-strict. We want enough
 * validation to protect the publish flow without making rollout brittle.
 */
function validatePricingPresentationDoc(doc) {
  const errors = [];

  if (!isPlainObject(doc)) {
    return {
      ok: false,
      errors: ["Pricing presentation doc must be an object."],
    };
  }

  if (normalizeNumber(doc.version, 0) <= 0) {
    errors.push("version must be a positive number.");
  }

  if (!isPlainObject(doc.planContent)) {
    errors.push("planContent must be an object.");
  } else {
    PRICING_PRESENTATION_PLAN_KEYS.forEach((planKey) => {
      const planContent = doc.planContent[planKey];

      if (!isPlainObject(planContent)) {
        errors.push(`planContent.${planKey} must be an object.`);
        return;
      }

      if (!Array.isArray(planContent.cardFeatureIds)) {
        errors.push(`planContent.${planKey}.cardFeatureIds must be an array.`);
      }
    });
  }

  if (!isPlainObject(doc.features)) {
    errors.push("features must be an object.");
  } else {
    Object.entries(doc.features).forEach(([featureId, feature]) => {
      const result = validateFeatureRecord(featureId, feature);
      if (!result.ok) {
        errors.push(...result.errors);
      }
    });
  }

  if (!Array.isArray(doc.compareCategories)) {
    errors.push("compareCategories must be an array.");
  } else {
    doc.compareCategories.forEach((cat, index) => {
      if (!isPlainObject(cat)) {
        errors.push(`compareCategories[${index}] must be an object.`);
        return;
      }

      if (!normalizeString(cat.key)) {
        errors.push(`compareCategories[${index}].key is required.`);
      }

      if (!normalizeString(cat.label)) {
        errors.push(`compareCategories[${index}].label is required.`);
      }
    });
  }

  return {
    ok: errors.length === 0,
    errors,
  };
}

/* ──────────────────────────────────────────────────────────────
 * Normalization
 * ─────────────────────────────────────────────────────────── */

/**
 * Normalize a feature record with defaults.
 */
function normalizeFeatureRecord(featureId, feature) {
  const base = createDefaultFeatureRecord(featureId);
  const src = isPlainObject(feature) ? feature : {};

  return {
    label: normalizeString(src.label, base.label),
    tooltip: normalizeString(src.tooltip, base.tooltip),
    categoryKey: normalizeString(src.categoryKey, base.categoryKey),
    compareOrder: normalizeNumber(src.compareOrder, base.compareOrder),
    includedIn: {
      standard:
        typeof src?.includedIn?.standard === "boolean" ?
          src.includedIn.standard :
          base.includedIn.standard,
      pro:
        typeof src?.includedIn?.pro === "boolean" ?
          src.includedIn.pro :
          base.includedIn.pro,
      enterprise:
        typeof src?.includedIn?.enterprise === "boolean" ?
          src.includedIn.enterprise :
          base.includedIn.enterprise,
    },
  };
}

/**
 * Normalize the full doc while preserving sensible defaults.
 *
 * This function is intended for:
 * - Firestore read hydration
 * - admin UI bootstrapping
 * - publish/merge pipelines
 */
function normalizePricingPresentationDoc(doc) {
  const src = isPlainObject(doc) ? doc : {};
  const base = createDefaultPricingPresentationDoc();

  const normalizedPlanContent = PRICING_PRESENTATION_PLAN_KEYS.reduce(
      (acc, planKey) => {
        const defaults = createDefaultPlanContent(planKey);
        const planSrc = isPlainObject(src?.planContent?.[planKey]) ?
          src.planContent[planKey] :
          {};

        acc[planKey] = {
          subtitle: normalizeString(planSrc.subtitle, defaults.subtitle),
          bestFor: normalizeString(planSrc.bestFor, defaults.bestFor),
          featureIntro: normalizeString(
              planSrc.featureIntro,
              defaults.featureIntro,
          ),
          cardFeatureIds: Array.isArray(planSrc.cardFeatureIds) ?
            planSrc.cardFeatureIds.filter(Boolean).map(String) :
            defaults.cardFeatureIds,
        };

        return acc;
      },
      {},
  );

  const featureSource = isPlainObject(src.features) ?
    src.features :
    base.features;

  const normalizedFeatures = Object.entries(featureSource).reduce(
      (acc, [featureId, feature]) => {
        acc[featureId] = normalizeFeatureRecord(featureId, feature);
        return acc;
      },
      {},
  );

  const compareSource = Array.isArray(src.compareCategories) ?
    src.compareCategories :
    base.compareCategories;

  const normalizedCompareCategories = compareSource
      .filter(isPlainObject)
      .map((cat) => ({
        key: normalizeString(cat.key),
        label: normalizeString(cat.label),
        order: normalizeNumber(cat.order, 0),
      }))
      .filter((cat) => cat.key && cat.label)
      .sort(sortByOrder);

  return {
    version: normalizeNumber(src.version, base.version),
    updatedAt: src.updatedAt || null,
    updatedBy: isPlainObject(src.updatedBy) ?
      {
        uid: src.updatedBy.uid || null,
        email: src.updatedBy.email || null,
        role: src.updatedBy.role || null,
      } :
      base.updatedBy,
    planContent: normalizedPlanContent,
    features: normalizedFeatures,
    compareCategories: normalizedCompareCategories,
  };
}

/* ──────────────────────────────────────────────────────────────
 * Exports
 * ─────────────────────────────────────────────────────────── */

module.exports = {
  PRICING_PRESENTATION_VERSION,
  PRICING_PRESENTATION_DOC_PATH,
  ALLOWED_PRICING_PRESENTATION_EDITOR_ROLES,
  PRICING_PRESENTATION_PLAN_KEYS,
  DEFAULT_COMPARE_CATEGORIES,
  DEFAULT_PLAN_CONTENT,
  DEFAULT_FEATURES,
  PRICING_PRESENTATION_DEFAULTS,

  getPricingPresentationRole,
  canEditPricingPresentation,
  canReadPricingPresentation,

  createDefaultPricingPresentationDoc,
  createDefaultPlanContent,
  createDefaultFeatureRecord,

  validateFeatureRecord,
  validatePricingPresentationDoc,

  normalizeFeatureRecord,
  normalizePricingPresentationDoc,
};